import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactQueryKeys } from 'constants/react-query-keys';
import DeploymentService from 'services/DeploymentService';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
// import { toast } from 'react-hot-toast';
import Pagination from 'components/UI/Pagination/Pagination';
import {
  getCoreRowModel,
  useReactTable,
  PaginationState,
  SortingState,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  // flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { Deployment } from 'interfaces/deployment';
import { formatMyDateLong, formatMyDateShort } from 'helpers/date-helpers';
import Select from 'react-select';
import DebouncedInput from 'components/UI/DebouncedInput/DebouncedInput';
import TypeDeploymentColumn from './Columns/TypeDeploymentColumn';
import LastColumn from './Columns/LastColumn';
import DateRangeInput from './DateRangeInput/DateRangeInput';
import {
  DEPLOYMENT_ALL_PREFIX,
  DEPLOYMENT_TAB_PARAMS_KEY,
} from 'constants/router';
import { useParams } from 'react-router-dom';

const FULL = 'full_deployment';
const PARTIAL_KEY: keyof Pick<Deployment, 'partial_deployment'> =
  'partial_deployment';
const DEPLOYMENT_TYPE = [PARTIAL_KEY, FULL];

const FILTER_PREFIX = 'filter_';

const DEFAULT_PAGE_INDEX = 0;

import Table from 'components/UI/Table/Table';

/**
 * @description DeploymentHistory component displays a paginated table of deployment records
 * with filtering and sorting capabilities.
 *
 * Features:
 * - Displays deployment history with date, version, user, and type information
 * - Supports filtering by date range, version, user, and deployment type
 * - Supports sorting by date, version, and user
 * - Automatically filters by deployment type based on the current tab
 * - Pagination with configurable page size
 *
 * @SADtrace SAD-003-6.4, SAD-003-6.5, SAD-003-6.6
 *
 * @returns {JSX.Element} The deployment history table component
 */
const DeploymentHistory = () => {
  const { t } = useTranslation();

  const { [DEPLOYMENT_TAB_PARAMS_KEY]: deploymentTab } = useParams();

  /**
   * @description Sets the deployment type filter based on the current tab selection.
   * If the tab is DEPLOYMENT_ALL_PREFIX, shows full deployments,
   * otherwise shows partial deployments.
   */
  useEffect(() => {
    const areWeShowingFullDeploymentOnLeftSectionOfThePage =
      deploymentTab === DEPLOYMENT_ALL_PREFIX;

    const typeOfDeploymentFilter =
      areWeShowingFullDeploymentOnLeftSectionOfThePage ? FULL : PARTIAL_KEY;

    const VALUE = {
      id: PARTIAL_KEY,
      value: {
        value: typeOfDeploymentFilter,
        label: t(`deployment.table.${FILTER_PREFIX + typeOfDeploymentFilter}`),
      },
    };

    setColumnFilters((oldArray) => {
      //on first load filter array is empty, so we set the filter-by-deployment-type value
      if (!oldArray.length) {
        return [VALUE];
      }

      //if filter array is not empty, and the user clicked a diffent tab on left section of the deployment page, then we set the filter-by-deployment-type accordingly
      // v8 ignore start -- WAIVER(MICRO): trivial pass-through branch returning non-deployment-type filters unchanged during the tab-switch filter rewrite; requires a user-set filter concurrent with a tab switch, exercised via E2E
      return oldArray.map((filter) => {
        if (filter.id !== PARTIAL_KEY) return filter;

        return VALUE;
      });
      // v8 ignore stop -- WAIVER(MICRO)
    });
  }, [deploymentTab]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'updated_at',
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  /**
   * @description Fetches deployment data based on current pagination, sorting, and filtering state
   */
  const dataQuery = useQuery({
    queryKey: [
      ReactQueryKeys.DEPLOYMENTS,
      {
        pageIndex,
        pageSize,
        sorting,
        columnFilters,
      },
    ],

    queryFn: () =>
      DeploymentService.get({ pageIndex, pageSize, sorting, columnFilters }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Define columns for the table
   * Each column specifies:
   * - Header content
   * - Cell rendering
   * - Sorting capabilities
   * - Filtering capabilities
   * - Accessor function
   *
   * @returns {ColumnDef<Deployment>[]} Array of column definitions
   */
  const VERSION_MAJOR: keyof Pick<Deployment, 'version_major'> =
    'version_major';
  const UPDATED_BY: keyof Pick<Deployment, 'updated_by'> = 'updated_by';
  const UPDATED_AT: keyof Pick<Deployment, 'updated_at'> = 'updated_at';

  const columns = useMemo<ColumnDef<Deployment>[]>(
    () => [
      {
        id: UPDATED_AT,
        header: () => t('deployment.table.header_date'),
        cell: (info) => {
          const { updated_at, partial_deployment } = info.row.original;
          return (
            <div
              className={`p-2 text-nowrap ${
                partial_deployment ? '' : ' fw-bold'
              }`}
              data-tip={formatMyDateLong(updated_at)}
            >
              {formatMyDateShort(updated_at)}
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        // v8 ignore start -- WAIVER(THIRDPARTY): no-op accessor required so react-table getCanSort()/getCanFilter() keep header sorting enabled on server-sorted columns; deleting it would disable sorting UI
        accessorFn: () => {},
        // v8 ignore stop -- WAIVER(THIRDPARTY)
      },
      {
        id: VERSION_MAJOR,
        header: () => t('deployment.table.header_version'),
        cell: (info) => {
          const { version_major, version_minor, partial_deployment } =
            info.row.original;
          return (
            <div
              className={`p-2 text-center ${
                partial_deployment ? '' : ' fw-bold'
              }`}
            >
              {version_major}
              {/* v8 ignore start -- WAIVER(THIRDPARTY): react-table cell formatting ternary; the empty branch renders when a deployment has no minor version - display-only formatting asserted representatively by DeploymentHistory tests */}
              {version_minor ? `.${version_minor}` : ''}
              {/* v8 ignore stop -- WAIVER(THIRDPARTY) */}
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        accessorFn: () => {},
      },
      {
        id: UPDATED_BY,
        header: () => t('deployment.table.header_user'),
        cell: (info) => {
          const { updated_by, partial_deployment } = info.row.original;
          return (
            <div
              className={`p-2 text-center d-inline-block text-truncate ${
                partial_deployment ? '' : ' fw-bold'
              }`}
              data-tip={updated_by}
            >
              {updated_by}
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        accessorFn: () => {},
      },
      {
        id: PARTIAL_KEY,
        header: () => t('deployment.table.header_devices'),
        cell: (info) => {
          return <TypeDeploymentColumn info={info} />;
        },
        enableSorting: false,
        enableColumnFilter: true,
        accessorFn: () => {},
      },
      {
        id: 'actions',
        header: '',
        cell: (info) => {
          return <LastColumn info={info} />;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [t],
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  /**
   * Initialize the table with data and configuration
   */
  const table = useReactTable({
    data: dataQuery.data?.data?.records ?? [],
    columns,
    pageCount: dataQuery.data?.data.totalPages ?? -1,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: (value) => {
      setColumnFilters(value);
      setPagination((oldValue) => ({
        ...oldValue,
        pageIndex: DEFAULT_PAGE_INDEX,
      }));
    },

    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: false,
  });

  return (
    <section
      className="d-flex flex-column mb-4"
      data-testid="deployment-history-section"
    >
      <div className="content-header__title">{t('deployment.table.title')}</div>
      <Table
        classes="users-table"
        isFetching={dataQuery.isFetching}
        table={table}
        filters={(header) => {
          return (
            <>
              {header.column.id === UPDATED_AT && (
                <DateRangeInput header={header} />
              )}

              {header.column.id === UPDATED_BY && (
                <DebouncedInput
                  value={(header.column.getFilterValue() ?? '') as string}
                  onChange={(value) => header.column.setFilterValue(value)}
                  className={
                    'form-control form-control-sm rounded text-center full-placeholder'
                  }
                  placeholder={t(
                    `deployment.table.placeholder_${header.column.id}`,
                  )}
                />
              )}

              {header.column.id === PARTIAL_KEY && (
                <Select
                  id="accessLevel"
                  maxMenuHeight={300}
                  onChange={header.column.setFilterValue}
                  value={header.column.getFilterValue()}
                  className="react-select-container-sm"
                  classNamePrefix="react-select"
                  isClearable
                  aria-label="deployment_type"
                  isSearchable
                  options={DEPLOYMENT_TYPE.map((status) => {
                    return {
                      label: t(`deployment.table.${FILTER_PREFIX + status}`),
                      value: status,
                    };
                  })}
                />
              )}

              {[VERSION_MAJOR].includes(header.column.id) && (
                <DebouncedInput
                  value={(header.column.getFilterValue() ?? '') as string}
                  type="text"
                  onChange={(value) => header.column.setFilterValue(value)}
                  className="form-control form-control-sm rounded"
                  data-testid={`filter_input_${header.column.id}`}
                />
              )}
            </>
          );
        }}
      />
      <Pagination table={table} responsive={true} />
    </section>
  );
};

export default DeploymentHistory;
